import { LOCATION_CHANGE, push, replace, RouterState } from 'connected-react-router';

import { SagaIterator } from 'redux-saga';
import { call, put, race, select, take, takeEvery, takeLatest } from 'redux-saga/effects';

import { Request } from '../../api/request';
import { API_URL, ENDPOINTS } from '../../constants/api';
import { IFRAME_PREVIEW_CAMPAIGN_ID } from '../../constants/http';
import { IRouterActions, ROUTE_NAMES, ROUTE_TEMPLATES } from '../../constants/routes';
import * as Analytics from '../../hooks/useAnalytics';
import { IGetFormDataRequestMeta } from '../../types/country';
import { IResponse } from '../../types/http';
import { IInitParams } from '../../types/init';
import { ICampaign, ICampaignItem } from '../../types/pyg';
import { ClaimPageStepEnum } from '../../types/shell';
import { IInitKeys } from '../../types/shipping';
import {
  ActionsEnum,
  IAction,
  IAuthorizeReceiverPayload,
  IAuthorizeReceiverSuccessPayload,
  IError,
  IInitSuccessPayload,
  ISetKeysPayload,
  ISetWhiteLabelingPayload,
  TInitFailure,
} from '../../types/store';
import { buildPath } from '../../util/buildPath';
import { readDetectedCountry, readStoredUserCountry } from '../../util/country';
import {
  isMskuItem,
  parseInitPath,
  parseInitQuery,
  splitInventoryByQuantity,
  validateInitQuery,
} from '../../util/helpers';
import { setCustomizableItems, setPickedCampaignId, submitPickYourGiftStep } from '../actions/form';
import * as InitActions from '../actions/init';
import { loadIframeInitDataAction } from '../actions/init';
import * as KeysActions from '../actions/keys';
import { selectIsBudgetOnlySend, selectIsKitChoiceSend, selectIsMarketplaceOnlySend } from '../selectors/derived';
import {
  selectActiveInitRequestId,
  selectCampaigns,
  selectIsFormSubmitted,
  selectIsLoading,
  selectItems,
  selectUserId,
} from '../selectors/init';
import { selectInitKeys } from '../selectors/keys';
import {
  isAuthChallengeFailure,
  isMarketplaceIneligibleFailure,
  prepareAuthHeadersSaga,
  showUsOnlyScreenSaga,
} from './helpers';

function* initSaga(action?: IAction<IInitParams>): SagaIterator {
  const payload = action?.payload;

  if (!payload) {
    yield call(terminateSaga);

    return;
  }

  const { initString, pathname } = payload;

  const isIframeRoot = pathname === ROUTE_NAMES.iframe;
  const isIframePath = isIframeRoot || pathname?.startsWith?.(`/o/${IFRAME_PREVIEW_CAMPAIGN_ID}/`);

  if (isIframePath) {
    // The preview is driven entirely by postMessage from the builder — no fetch,
    // no auth. Framing is restricted by the CSP frame-ancestors header and every
    // inbound message is origin-checked against IFRAME_ALLOWED_ORIGINS in
    // useIframe. We must NOT gate this on document.referrer: on a cached,
    // client-side navigation from the builder's list the referrer is often empty
    // (strict-origin policy + instant load), which previously redirected the
    // preview to the error page and deadlocked the ready/ping handshake.
    yield put(KeysActions.setKeysAction({ one_link_id: IFRAME_PREVIEW_CAMPAIGN_ID }));
    yield put(loadIframeInitDataAction());

    const targetPath = isIframeRoot
      ? buildPath(ROUTE_TEMPLATES.ONE_LINK, {
          oneLinkId: IFRAME_PREVIEW_CAMPAIGN_ID,
          step: ClaimPageStepEnum.PickYourGift,
        })
      : (pathname as string);
    yield put(replace(targetPath));

    return;
  }

  // 1. the path names the claim; the query string is only the legacy link format and can never
  // override a claim identified by the path. Fall back to redux when neither is given.
  let keys: IInitKeys;
  if (pathname || initString) {
    keys = parseInitPath(pathname ?? '');

    if (!validateInitQuery(keys) && initString) {
      keys = parseInitQuery(initString);
    }
  } else {
    keys = yield select(selectInitKeys);
  }

  if (!validateInitQuery(keys)) {
    yield call(terminateSaga);

    return;
  }

  try {
    const isLoading: boolean = yield select(selectIsLoading);
    if (isLoading) {
      return;
    }

    // 2. make the init request
    if (Object.keys(keys).length) {
      yield put(KeysActions.setKeysAction(keys));

      const storedCountry: string | null = yield call(readStoredUserCountry, keys);
      if (storedCountry) {
        yield put(InitActions.countryChangeCommitted(storedCountry));
      }

      yield put(InitActions.getFormDataRequestAction(keys));

      // 3. handle success and failure cases
      const [success, failure] = yield race([
        take(ActionsEnum.GetFormDataSuccess),
        take(ActionsEnum.GetFormDataFailure),
      ]);

      if (success) {
        // 4. if success - redirect user to the proper step
        yield call(initRedirectSaga);
        const userId = yield select(selectUserId);
        Analytics.config({ userId });
      }

      if (failure) {
        const { ds_customized_texts, org_info, sender_info } = failure.payload;

        if (isMarketplaceIneligibleFailure(failure.payload)) {
          return;
        }

        yield call(Analytics.apiException, failure.payload);

        // 5. if failure - check if the error is related to the password
        if (isAuthChallengeFailure(failure.payload)) {
          // 5.1 if password or 2fa is required - redirect user to the auth
          yield put(replace(ROUTE_NAMES.authentication));
          yield put(KeysActions.updateKeysAction({ allowed_recipient_uid: '' }));
          yield put(
            InitActions.setWhiteLabeling({ ds_customized_texts, org_info, sender_info } as ISetWhiteLabelingPayload),
          );
        } else {
          // 5.2 if failure - deny the app start
          yield call(terminateSaga, failure.payload);
        }
      }
    } else {
      // if there are no keys - deny the app start
      yield call(terminateSaga);
    }
  } catch (e) {
    yield call(terminateSaga, e);
    yield call(Analytics.exception, e);
  }
}

function* isInitRequestSupersededSaga(meta?: IGetFormDataRequestMeta): SagaIterator<boolean> {
  if (!meta?.requestId) {
    return false;
  }

  const activeRequestId: string | null = yield select(selectActiveInitRequestId);

  return activeRequestId !== meta.requestId;
}

export function* getFormDataRequestWorkerSaga(action: IAction<ISetKeysPayload, IGetFormDataRequestMeta>) {
  const { resolve, reject } = action.payload || {};
  const meta = action.meta ?? undefined;
  let initResponse: IResponse<IInitSuccessPayload | IError>;

  try {
    let queryParams;
    let endpoint;
    const headers: Record<string, string> = yield call(prepareAuthHeadersSaga);

    const { one_link_id, key, order_id } = action.payload || {};

    if (order_id && key) {
      queryParams = { order_id, key };
      endpoint = `${API_URL}${ENDPOINTS.init}`;
    } else if (one_link_id) {
      endpoint = `${API_URL}${ENDPOINTS.oneLinkInit(one_link_id)}`;
    } else {
      yield put(InitActions.getFormDataFailureAction({ message: 'An unexpected error has occurred.' }));
    }

    if (!endpoint) {
      console.warn('No data was provided for the request');

      return;
    }

    initResponse = yield call<any>(Request, { headers, endpoint, queryParams });
  } catch (e) {
    yield call(failRequestSaga, e, meta, reject);

    return;
  }

  let isOutcomeDispatched = false;
  let isNavigationOwned = !!meta?.afterAuth;

  try {
    const isSuperseded: boolean = yield call(isInitRequestSupersededSaga, meta);
    if (isSuperseded) {
      return;
    }

    const detectedCountry = readDetectedCountry(initResponse.headers);

    if (initResponse.ok) {
      const { body } = initResponse as IResponse<IInitSuccessPayload>;

      if (resolve) {
        yield call(resolve, body);
      }

      yield put(InitActions.setDetectedCountry(detectedCountry));
      yield put(InitActions.getFormDataSuccessAction(body, meta));
      isOutcomeDispatched = true;
      yield call(initRedirectSaga);
    } else {
      const { body, status } = initResponse;

      if (reject) {
        yield call(reject, body);
      }
      if (detectedCountry) {
        yield put(InitActions.setDetectedCountry(detectedCountry));
      }
      const failure: TInitFailure = { ...body, status };

      yield put(InitActions.getFormDataFailureAction(failure, meta));
      isOutcomeDispatched = true;

      if (!meta?.countryChange) {
        if (isMarketplaceIneligibleFailure(failure)) {
          isNavigationOwned = true;
          yield call(showUsOnlyScreenSaga, failure);
        } else if (meta?.afterAuth && !isAuthChallengeFailure(failure)) {
          yield call(failAfterAuthSaga, failure);
        }
      }
    }
  } catch (e) {
    yield call(failResponseHandlingSaga, e, { isNavigationOwned, isOutcomeDispatched }, meta);
  }
}

function* failRequestSaga(error: unknown, meta?: IGetFormDataRequestMeta, reject?: (e: unknown) => void): SagaIterator {
  try {
    const isSuperseded: boolean = yield call(isInitRequestSupersededSaga, meta);
    if (isSuperseded) {
      return;
    }
    if (reject) {
      yield call(reject, error);
    }
    yield put(InitActions.getFormDataFailureAction(error, meta));
  } catch (unhandled) {
    console.error(unhandled);
  }

  if (meta?.afterAuth) {
    yield call(failAfterAuthSaga, error);
  }
}

function* failAfterAuthSaga(failure: unknown): SagaIterator {
  try {
    yield call(Analytics.apiException, failure);
    yield call(terminateSaga, failure);
  } catch (unhandled) {
    console.error(unhandled);
  }
}

function* failResponseHandlingSaga(
  error: unknown,
  { isNavigationOwned, isOutcomeDispatched }: { isNavigationOwned: boolean; isOutcomeDispatched: boolean },
  meta?: IGetFormDataRequestMeta,
): SagaIterator {
  try {
    yield call(Analytics.exception, error);

    if (isNavigationOwned) {
      yield call(terminateSaga, error);
    } else if (!isOutcomeDispatched) {
      yield put(InitActions.getFormDataFailureAction(error, meta));
    }
  } catch (unhandled) {
    console.error(unhandled);
  }
}

export function* authorizeReceiverWorkerSaga(action: IAction<IAuthorizeReceiverPayload>) {
  const { resolve, reject } = action.payload || {};
  const keys: IInitKeys = yield select(selectInitKeys);

  try {
    const endpoint = `${API_URL}${ENDPOINTS.authorizeReceiver}`;

    const response: IResponse<IAuthorizeReceiverSuccessPayload | IError> = yield call<any>(Request, {
      endpoint,
      method: 'POST',
      body: JSON.stringify(action.payload),
    });

    if (response.ok) {
      const { body } = response as IResponse<IAuthorizeReceiverSuccessPayload>;

      if (resolve) {
        yield call(resolve, body);
      }

      const { id } = body;
      const { action: authAction } = action.payload || {};

      if (id && authAction === 'init') {
        yield put(KeysActions.updateKeysAction({ allowed_recipient_uid: id }));
      }

      if (authAction === 'verify') {
        yield put(InitActions.getFormDataRequestAction(keys, { afterAuth: true }));
      }
    } else {
      const { body } = response;
      if (reject) {
        yield call(reject, body);
      }
    }
  } catch (e) {
    if (reject) {
      yield call(reject, e);
    }
  }
}

function* terminateSaga(error?: any) {
  // pass an init error via the router state
  yield put(push(ROUTE_NAMES.error, error));
}

export function* initRedirectSaga(): SagaIterator<string> {
  const campaigns: ICampaign[] | null = yield select(selectCampaigns);
  const items: ICampaignItem[] | null = yield select(selectItems);
  const isKitChoiceSend: boolean = yield select(selectIsKitChoiceSend);
  const isBudgetOnlySend: boolean = yield select(selectIsBudgetOnlySend);
  const isMarketplaceOnlySend: boolean = yield select(selectIsMarketplaceOnlySend);
  const keys: IInitKeys = yield select(selectInitKeys);
  const { one_link_id, order_id, key } = keys;
  const physicalItems = items?.filter((item) => !item?.sku_options?.length);

  const isPhysicalSendWithCustomizableItems =
    items &&
    items.length &&
    items.every((item) => !item?.sku_options?.length) &&
    items.some((item) => item?.customization?.item_customization_allowed);

  let stepName = ROUTE_NAMES.address;

  // The kit-choice screen is a PYG variation; a budget-only send opens on the catalog instead.
  if ((campaigns && campaigns.length) || isKitChoiceSend) {
    stepName = ROUTE_NAMES.pyg;
  }

  if (isBudgetOnlySend) {
    stepName = ROUTE_NAMES.marketplace;
  }

  // Marketplace items route on the send's shape, not on sku_options: variants are still being
  // fetched at this point. A kit-plus-budget send keeps its PYG landing so the recipient can
  // still choose the catalog instead of the kit.
  const isMarketplaceOptionsStep = isMarketplaceOnlySend && !isKitChoiceSend;
  const hasMskuItems = !!items?.length && items.some(isMskuItem);

  if (isMarketplaceOptionsStep || (hasMskuItems && !isKitChoiceSend)) {
    stepName = ROUTE_NAMES.pickMsku;
  }

  // A single marketplace campaign has nothing to pick, so PYG is skipped — but its items only
  // reach inventory_items through SubmitPYG. Adopt it here or the options step renders empty.
  const singleCampaignId = isMarketplaceOptionsStep && campaigns?.length === 1 ? campaigns[0].id : null;

  if (singleCampaignId) {
    yield put(setPickedCampaignId(singleCampaignId));
    yield put(submitPickYourGiftStep({ campaignId: singleCampaignId }));
  }

  if (isPhysicalSendWithCustomizableItems && !isKitChoiceSend) {
    const customizableItems = physicalItems?.filter((item) => item?.customization?.item_customization_allowed);
    yield put(setCustomizableItems(splitInventoryByQuantity(customizableItems || [])));
    stepName = ROUTE_NAMES.customizeItems;
  }

  if (one_link_id) {
    const path = buildPath(ROUTE_TEMPLATES.ONE_LINK, {
      oneLinkId: one_link_id,
      step: stepName.replace('/', ''),
    });

    yield put(replace(path));
  } else {
    const path = buildPath(ROUTE_TEMPLATES.SEND, {
      orderId: order_id || '',
      key: key || '',
      step: stepName,
    });
    yield put(replace(path));
  }

  return stepName;
}

function* locationWorkerSaga(action: IAction<RouterState>): Generator<any, any, any> {
  const { action: routerAction } = action.payload || {};

  const isFormSubmitted = yield select(selectIsFormSubmitted);

  if (isFormSubmitted && routerAction === IRouterActions.BACK) {
    yield call(initSaga);
  }

  yield call(Analytics.page);
}

const sagas = {
  *watchInitApp() {
    yield takeLatest(ActionsEnum.InitApp, initSaga);
  },
  *watchInitRequest() {
    yield takeEvery(ActionsEnum.GetFormDataRequest, getFormDataRequestWorkerSaga);
  },
  *watchAuthorizeReceiverRequest() {
    yield takeEvery(ActionsEnum.AuthorizeReceiverRequest, authorizeReceiverWorkerSaga);
  },
  *watchLocationChange() {
    yield takeEvery(LOCATION_CHANGE, locationWorkerSaga);
  },
};

export default sagas;
